/**
 * MyLibrary.jsx — Personal library landing as a books index.
 *
 * Feb 9 2026 user spec (latest): *"make personal library link into a
 * page where documents can be arranged into single books or continuous
 * series of books. words only. make it a tap to open like the other
 * ones and ensure documents and typed or pasted docs can actually save
 * and be added to library"*.
 *
 * Layout (mirrors the Library gate):
 *   • Sharp serif header ("My Library.")
 *   • Continue Reading rail — most-recently-opened books
 *   • Big "Create book" CTA tile
 *   • Each book → BigPill (tap to open the dedicated /my-library/book/:id page)
 *
 * Scan upload moved to the book-detail page; landing is books-only
 * (text-driven) per the latest spec ("words only").
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, BookOpen, Plus, ChevronRight, Layers, FileText, Sparkles, X,
  Upload, FileUp,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import TrialGateBanner from '../components/TrialGateBanner';
import useTrialGate from '../hooks/useTrialGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); }
  catch { return ''; }
};

// ── Big tap-to-open tile for a book (matches /library aesthetic) ───
const BookTile = ({ book, onOpen }) => {
  // Each book gets either an amber (single doc) or indigo (series)
  // accent — same vocabulary the Library uses to colour-code ebooks
  // vs audio.
  const A = book.is_series
    ? { border: 'border-indigo-400/35 hover:border-indigo-300/60 focus-visible:ring-indigo-300/40',
        text: 'text-indigo-300', iconBg: 'bg-indigo-400/15 border-indigo-400/30',
        bar: 'from-indigo-400 to-indigo-200', label: 'Series' }
    : { border: 'border-amber-400/35 hover:border-amber-300/60 focus-visible:ring-amber-300/40',
        text: 'text-amber-300', iconBg: 'bg-amber-400/15 border-amber-400/30',
        bar: 'from-amber-400 to-amber-200', label: 'Single doc' };
  const Icon = book.is_series ? Layers : FileText;
  return (
    <button
      type="button"
      onClick={() => onOpen(book)}
      data-testid={`my-library-book-${book.book_id}`}
      className={`group relative w-full text-left rounded-2xl overflow-hidden border ${A.border} bg-[#12131A] active:scale-[0.995] transition-[transform,border-color] focus:outline-none focus-visible:ring-2`}
    >
      <div className="px-4 py-5 sm:px-6 sm:py-7 flex items-center gap-4">
        <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl border ${A.iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${A.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`eyebrow ${A.text}`}>{A.label} · Tap to open</p>
          <p className="font-display text-xl sm:text-2xl font-black text-white tracking-tight leading-tight mt-1 truncate">{book.title}</p>
          <p className="text-[11px] text-white/55 mt-1 tabular-nums">
            {book.chapter_count} {book.chapter_count === 1 ? 'chapter' : 'chapters'} · {(book.total_word_count || 0).toLocaleString()} words
            {book.last_opened_at && <span className="text-white/30"> · last opened {fmtDate(book.last_opened_at)}</span>}
          </p>
        </div>
        <ChevronRight className={`w-5 h-5 ${A.text} opacity-60 group-hover:opacity-100 transition-opacity`} />
      </div>
      <div className={`absolute right-0 top-0 h-full w-[3px] bg-gradient-to-b ${A.bar}`} />
    </button>
  );
};

// ── Create-book modal ─────────────────────────────────────────────
const CreateBookModal = ({ open, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [isSeries, setIsSeries] = useState(false);
  const [chTitle, setChTitle] = useState('Chapter 1');
  const [chText, setChText] = useState('');
  const [busy, setBusy] = useState(false);
  const reset = () => { setTitle(''); setIsSeries(false); setChTitle('Chapter 1'); setChText(''); };

  useEffect(() => { if (!open) reset(); }, [open]);

  const submit = async () => {
    if (!title.trim()) { toast.error('Give the book a title'); return; }
    if (!chText.trim()) { toast.error('Add some opening words to the first chapter'); return; }
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/personal-library/books`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          is_series: isSeries,
          first_chapter: { title: chTitle.trim() || 'Chapter 1', text: chText.trim(), source_type: 'type' },
        }),
      });
      if (r.status === 403) {
        toast.error('Personal library is a Premium feature.');
        return;
      }
      if (!r.ok) { toast.error('Could not create book'); return; }
      const d = await r.json();
      toast.success(`Book "${d.book.title}" created`);
      onCreated(d.book);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={onClose}
      data-testid="my-library-create-modal"
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-[#12131A] border border-white/10 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-1">
            <p className="eyebrow text-amber-300">New book</p>
            <h3 className="font-display text-2xl font-black text-white mt-0.5 tracking-tight">Add to your library</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-white" data-testid="my-library-create-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="block mb-3">
          <p className="eyebrow text-white/40 mb-1">Book title</p>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Notes on Lagos"
            maxLength={200}
            className="w-full bg-[#0B0C10] border border-white/10 rounded-xl text-white text-sm px-3 py-2.5 focus:outline-none focus:border-amber-400/50"
            data-testid="my-library-create-title"
          />
        </label>

        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setIsSeries(false)}
            className={`flex-1 rounded-xl border px-3 py-2.5 text-left transition ${!isSeries ? 'border-amber-400 bg-amber-400/10' : 'border-white/10 hover:border-white/30 bg-white/[0.02]'}`}
            data-testid="my-library-create-single"
          >
            <p className={`text-xs font-black uppercase tracking-widest ${!isSeries ? 'text-amber-300' : 'text-white/55'}`}>Single doc</p>
            <p className="text-[11px] text-white/55 mt-0.5">One piece of writing</p>
          </button>
          <button
            type="button"
            onClick={() => setIsSeries(true)}
            className={`flex-1 rounded-xl border px-3 py-2.5 text-left transition ${isSeries ? 'border-indigo-400 bg-indigo-400/10' : 'border-white/10 hover:border-white/30 bg-white/[0.02]'}`}
            data-testid="my-library-create-series"
          >
            <p className={`text-xs font-black uppercase tracking-widest ${isSeries ? 'text-indigo-300' : 'text-white/55'}`}>Series</p>
            <p className="text-[11px] text-white/55 mt-0.5">Multi-chapter / continuous</p>
          </button>
        </div>

        <label className="block mb-3">
          <p className="eyebrow text-white/40 mb-1">First chapter title</p>
          <input
            value={chTitle}
            onChange={(e) => setChTitle(e.target.value)}
            placeholder="Chapter 1"
            maxLength={200}
            className="w-full bg-[#0B0C10] border border-white/10 rounded-xl text-white text-sm px-3 py-2.5 focus:outline-none focus:border-amber-400/50"
            data-testid="my-library-create-chtitle"
          />
        </label>

        <label className="block mb-4">
          <p className="eyebrow text-white/40 mb-1">First chapter — type or paste</p>
          <textarea
            value={chText}
            onChange={(e) => setChText(e.target.value)}
            placeholder="Paste or type the opening pages here…"
            rows={6}
            className="w-full bg-[#0B0C10] border border-white/10 rounded-xl text-white text-sm px-3 py-2.5 focus:outline-none focus:border-amber-400/50 resize-y"
            data-testid="my-library-create-chtext"
          />
          <p className="text-[10px] text-white/40 mt-1 tabular-nums">
            {chText.split(/\s+/).filter(Boolean).length} words
          </p>
        </label>

        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="w-full bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-black py-3 rounded-full uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
          data-testid="my-library-create-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Create book
        </button>
      </div>
    </div>
  );
};

const MyLibrary = () => {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [quota, setQuota] = useState(null);
  const fileInputRef = useRef(null);
  // Trial gate — peek only so landing on /my-library doesn't count as
  // "first access". First write (create book / upload doc) starts the
  // 24h clock via the backend `require_feature_access` call.
  const { status: trialStatus, expired: trialExpired, refresh: refreshTrial, handle402 } =
    useTrialGate('my_library', { peek: true });

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/personal-library/books`);
      if (r.status === 401) { setBooks([]); return; }
      const d = await r.json();
      setBooks(d.books || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const uploadDoc = async (file) => {
    if (!file) return;
    if (trialExpired) {
      toast.error('Free trial ended. Upgrade to Premium to upload docs.', {
        action: { label: 'Upgrade', onClick: () => navigate('/premium') },
      });
      return;
    }
    const ext = ('.' + (file.name.split('.').pop() || '')).toLowerCase();
    if (!['.pdf', '.docx'].includes(ext)) {
      toast.error('Only PDF or Word (.docx) files are supported');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large — 10MB limit per upload');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('title', file.name.replace(/\.(pdf|docx)$/i, ''));
      const r = await authFetch(`${BACKEND_URL}/api/personal-library/upload-doc`, {
        method: 'POST',
        body: form,
      });
      if (await handle402(r)) return;
      if (r.status === 409) {
        const d = await r.json();
        toast.error(d.detail || 'Upload limit reached');
        return;
      }
      if (r.status === 422) {
        toast.error('Could not read text from that file. If it\'s scanned, use the Scan option instead.');
        return;
      }
      if (!r.ok) {
        let msg = 'Upload failed';
        try { const d = await r.json(); if (d?.detail) msg = d.detail; } catch { /* no-op */ }
        toast.error(msg);
        return;
      }
      const d = await r.json();
      setQuota(d.quota || null);
      toast.success(`"${d.item.title}" uploaded · ${d.item.word_count} words`);
      refreshTrial();
      // Prepend the fresh book to the shelf so the upload appears
      // immediately without a page reload. Falls back to full refresh
      // if the server didn't return a book payload (older builds).
      if (d.book) {
        setBooks((cur) => {
          const filtered = cur.filter((b) => b.book_id !== d.book.book_id);
          return [d.book, ...filtered];
        });
      } else {
        refresh();
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openBook = async (book) => {
    // Stamp opened-at so the rail re-orders, then navigate.
    try {
      await authFetch(`${BACKEND_URL}/api/personal-library/books/${book.book_id}/open`, { method: 'POST' });
    } catch { /* non-fatal */ }
    navigate(`/my-library/book/${book.book_id}`);
  };

  const onCreated = (book) => {
    setShowCreate(false);
    setBooks((cur) => [book, ...cur.filter((b) => b.book_id !== book.book_id)]);
    // Drop straight into the new book so the user can keep writing.
    navigate(`/my-library/book/${book.book_id}`);
  };

  // Continue Reading rail — most recent 6 books.
  const recent = books.slice(0, 6);

  return (
    <main className="sharp-bg max-w-3xl mx-auto px-4 py-6 pb-32 min-h-screen" data-testid="my-library-page">
      <header className="mb-6" data-testid="my-library-header">
        <p className="eyebrow text-amber-300">Your private shelf</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight leading-[1.02] mt-1">
          My Library<span className="text-amber-300">.</span>
        </h1>
        <p className="text-[11px] text-white/45 mt-1">
          Words only — type, paste, upload PDFs/Word docs, or organise into books. Private to you.
        </p>
      </header>

      {/* Feb 14 2026 — 1-day free-trial gate. Full paywall on expiry;
          soft strip while the clock is ticking. */}
      <TrialGateBanner status={trialStatus} feature="my_library" />

      {/* PDF / DOCX upload — sits alongside the "New book" pill. Puts
          the uploaded doc into the unfiled flat list; user can then
          slot it into a book from the doc's detail page. */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx"
        className="hidden"
        data-testid="my-library-upload-input"
        onChange={(e) => uploadDoc(e.target.files?.[0])}
      />
      <button
        type="button"
        onClick={() => !uploading && !trialExpired && fileInputRef.current?.click()}
        disabled={uploading || trialExpired}
        className="w-full mb-3 rounded-2xl border-2 border-dashed border-cyan-400/40 hover:border-cyan-300 disabled:opacity-50 bg-[#12131A] px-4 py-5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/40"
        data-testid="my-library-upload-btn"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-cyan-400/15 border border-cyan-400/30 grid place-items-center flex-shrink-0">
            {uploading ? (
              <Loader2 className="w-5 h-5 text-cyan-300 animate-spin" />
            ) : (
              <FileUp className="w-5 h-5 text-cyan-300" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="eyebrow text-cyan-300">Upload</p>
            <p className="font-display text-lg font-black text-white tracking-tight">
              {uploading ? 'Extracting text…' : 'PDF or Word doc'}
            </p>
            <p className="text-[11px] text-white/55 mt-0.5">
              10&nbsp;MB limit · 20 uploads per user
              {quota && (
                <span className="text-cyan-300/80 tabular-nums" data-testid="my-library-upload-quota">
                  {' '}· {quota.used}/{quota.limit} used
                </span>
              )}
            </p>
          </div>
        </div>
      </button>

      {recent.length > 0 && (
        <section className="mb-6" data-testid="my-library-continue-reading">
          <p className="eyebrow text-indigo-300 mb-2 inline-flex items-center gap-1.5">
            <BookOpen className="w-3 h-3" /> Continue Reading
          </p>
          <div className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1 snap-x no-scrollbar">
            {recent.map((b) => (
              <button
                key={b.book_id}
                type="button"
                onClick={() => openBook(b)}
                className="flex-shrink-0 w-44 sm:w-48 rounded-xl overflow-hidden bg-[#12131A] border border-white/8 hover:border-indigo-400/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/40 text-left transition-colors"
                data-testid={`my-library-continue-card-${b.book_id}`}
              >
                <div className="relative w-full aspect-[16/10] bg-[#0B0C10] flex items-center justify-center overflow-hidden">
                  {b.is_series
                    ? <Layers className="w-7 h-7 text-indigo-300 opacity-60" />
                    : <FileText className="w-7 h-7 text-amber-300 opacity-60" />
                  }
                </div>
                <div className="p-2.5">
                  <p className="font-display text-[13px] font-black text-white truncate tracking-tight">{b.title}</p>
                  <p className={`text-[10px] uppercase tracking-[0.2em] font-black mt-0.5 tabular-nums ${b.is_series ? 'text-indigo-300' : 'text-amber-300'}`}>
                    {b.chapter_count} ch · {(b.total_word_count || 0).toLocaleString()}w
                  </p>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Create-book pill — top of the books list so it's never lost */}
      <button
        type="button"
        onClick={() => setShowCreate(true)}
        className="w-full mb-3 rounded-2xl border-2 border-dashed border-amber-400/40 hover:border-amber-300 bg-[#12131A] px-4 py-5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40"
        data-testid="my-library-new-book-btn"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-amber-400/15 border border-amber-400/30 grid place-items-center flex-shrink-0">
            <Plus className="w-5 h-5 text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="eyebrow text-amber-300">Create</p>
            <p className="font-display text-lg font-black text-white tracking-tight">New book</p>
            <p className="text-[11px] text-white/55 mt-0.5">Type or paste a doc · choose Single or Series</p>
          </div>
        </div>
      </button>

      {/* Books list */}
      {loading ? (
        <p className="text-sm text-white/40 mt-6 text-center">Loading…</p>
      ) : books.length === 0 ? (
        <div className="text-center py-10 text-sm text-white/40" data-testid="my-library-empty">
          <Sparkles className="w-5 h-5 mx-auto mb-2 text-amber-300/60" />
          No books yet. Tap <span className="text-amber-300">New book</span> above to start.
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="my-library-books-list">
          {books.map((b) => <BookTile key={b.book_id} book={b} onOpen={openBook} />)}
        </div>
      )}

      <CreateBookModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={onCreated}
      />
    </main>
  );
};

export default MyLibrary;
